import React from "react";
import LegalShell from "@/components/LegalShell";
import SEO from "@/components/SEO";

export default function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Service | Postmoro"
        description="The terms that govern your use of Postmoro's AI-powered social media scheduling and publishing platform for Facebook, Instagram, LinkedIn, and X."
        path="/terms"
      />
      <LegalShell
      title="Terms of Service"
      subtitle="The agreement between you and Postmoro when you use our service."
      lastUpdated="August 2026"
      testidPrefix="terms"
    >
      <p>
        These Terms of Service (“Terms”) govern your access to and use of Postmoro,
        a service operated by <strong>Postmoro LLC</strong> (“Postmoro,” “we,” “us,” or
        “our”). By creating an account or using the service you agree to these Terms.
        If you do not agree, please do not use the service.
      </p>

      <h2>1. Subscriptions &amp; Billing</h2>
      <ul>
        <li>Postmoro offers Free Trial, Starter, Pro, and Business plans. Plan inclusions and limits are described on the Billing page.</li>
        <li>Paid subscriptions are billed in advance on a recurring basis (monthly unless otherwise specified) via Stripe.</li>
        <li>By starting a paid plan, you authorize recurring charges to your selected payment method until the subscription is canceled.</li>
        <li>
          Plans may include separate limits for <strong>AI generations</strong> and{" "}
          <strong>published posts</strong>. AI generations and published posts are counted
          separately and reset according to the billing period shown in your account.
        </li>
        <li>
          Except where required by law, payments are <strong>non-refundable</strong> once
          charged.
        </li>
        <li>
          Business plan &quot;unlimited&quot; features are subject to fair use, technical
          limits, platform API limits, abuse prevention, and reasonable usage restrictions.
        </li>
        <li>
          Support tickets are reviewed as soon as reasonably possible, but Postmoro does
          not guarantee a specific response time unless expressly stated in your plan.
        </li>
      </ul>

      <h2>2. Cancellation</h2>
      <ul>
        <li>You can cancel any paid subscription at any time from the Billing page (Manage subscription).</li>
        <li><strong>Cancellation takes effect at the end of the current billing period.</strong> You retain access to paid features until that date.</li>
        <li>Once your subscription ends, your account automatically reverts to the Free Trial plan and its limits apply going forward.</li>
        <li>We do not offer prorated refunds for partial billing periods unless required by law.</li>
      </ul>

      <h2>3. No Guaranteed Business Results</h2>
      <p>
        Postmoro is a productivity tool. We do <strong>not</strong> guarantee any specific
        business outcome, follower growth, sales lift, engagement rate, or other commercial
        result from posts created or scheduled by the service. You are responsible for your
        business outcomes.
      </p>

      <h2>4. Your Responsibility for Content</h2>
      <p>
        You are solely responsible for the content you publish through Postmoro, including:
      </p>
      <ul>
        <li>Reviewing AI-generated drafts before publishing.</li>
        <li>Ensuring you have the rights to any images, brand assets, or other materials you upload.</li>
        <li>Complying with the laws and platform rules that apply to your business and content.</li>
      </ul>

      <h2>5. Your Content</h2>
      <p>
        You retain ownership of the business information, uploaded images, past post examples,
        and content you provide to Postmoro. By using Postmoro, you grant us permission to
        process that content only as needed to provide the service, generate drafts, schedule
        posts, publish approved content, troubleshoot issues, and improve your account
        experience.
      </p>

      <h2>6. AI-Generated Content Disclaimer</h2>
      <p>
        Captions, hashtags, ideas, and images produced by Postmoro are generated by AI models.
        AI output can sometimes be inaccurate, biased, or unsuitable for your audience.
        <strong> Always review AI-generated content before publishing.</strong> Postmoro is not
        liable for content that you choose to publish.
      </p>

      <h2>7. Social Platform Limitations</h2>
      <ul>
        <li>Publishing requires that you connect a valid account on the target platform and grant the necessary permissions.</li>
        <li>Platform APIs (Meta, LinkedIn, X, etc.) impose their own rate limits, content policies, and availability constraints. Postmoro is not responsible for platform-side rejections, outages, or policy changes.</li>
        <li>Some publishing capabilities are gated by platform review processes (e.g. Meta App Review). Until approval is granted, certain platforms may be limited to draft/scheduled status only.</li>
      </ul>

      <h2>8. Acceptable Use</h2>
      <p>You agree NOT to use Postmoro to:</p>
      <ul>
        <li>Publish illegal, defamatory, hateful, harassing, or sexually explicit content.</li>
        <li>Impersonate another person or business, or misrepresent your affiliation with them.</li>
        <li>Send spam, malicious links, or scraped content that violates a third party&apos;s rights.</li>
        <li>Circumvent plan limits or attempt to reverse-engineer the service.</li>
      </ul>

      <h2>9. Account Termination</h2>
      <p>
        We may suspend or terminate your account if you violate these Terms or engage in
        activity that puts Postmoro, our customers, or our platform partners at risk. We will
        make a reasonable effort to notify you when possible.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Postmoro and its operators are not liable
        for any indirect, incidental, special, consequential, or punitive damages, or for any
        loss of profits or revenue, arising from your use of the service. Our total liability
        for any claim related to Postmoro is limited to the amount you paid us in the 12 months
        preceding the claim.
      </p>

      <h2>11. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be communicated via
        the service or by email. Your continued use of Postmoro after changes take effect
        constitutes acceptance of the updated Terms.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions? Email <a href="mailto:support@postmoro.com">support@postmoro.com</a>.
      </p>
    </LegalShell>
    </>
  );
}
